import { ChangeDetectionStrategy, Component, computed, DOCUMENT, effect, inject, input, signal, untracked, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivityEntityName, Entity, JobTemplate, JobTemplateQuestionCommandId, JobTemplateQuestionType, JobTemplateQuestionVisibilityCondition } from '@jms/model';
import { NgSelectModule } from '@ng-select/ng-select';
import { injectQueryClient } from '@ngneat/query';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { ToastService } from '@workbuddy/ui/services';
import { injectDialogRef } from '@workbuddy/ui/v2/blocks/dialog';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { lastValueFrom, tap } from 'rxjs';
import { JOB_TEMPLATE_QUESTION_QUERY_KEY } from '../../job.template.question.model';
import { jobTemplateQuestionVisibilityBridgeStyles, jobTemplateQuestionVisibilityStyleId } from './question.visibility.theme';

/** One question a rule may depend on, flattened to what the two dropdowns bind. */
interface ConditionQuestion extends Entity {
  options: Entity[];
}

/**
 * A separate dialog for the reason legacy keeps it one: the rule is about a question's place among its
 * siblings, not about what it asks. Only a Dropdown with options can be depended on — anything else
 * could never evaluate. A plain modal, because a manifest declares only create / edit / preview.
 */
@UntilDestroy()
@Component({
  selector: 'wb-job-template-question-visibility',
  standalone: true,
  templateUrl: './question.visibility.component.html',
  styleUrls: ['./question.visibility.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, NgSelectModule],
})
export class JobTemplateQuestionVisibilityComponent {

  public readonly $template = input.required<JobTemplate>({ alias: 'template' });
  public readonly $questionId = input.required<string>({ alias: 'questionId' });
  /** Host refresh once the rule is stored; the record carries the question it just changed. */
  public readonly $questionsChanged = input<() => void>(() => { }, { alias: 'questionsChanged' });

  /** Sentinel for "no rule". Stored as `showCondition: null`, the way legacy writes it. */
  protected readonly alwaysId = '__always__';

  private readonly entityData = inject(EntityDataServiceRaw);
  private readonly queryClient = injectQueryClient();
  private readonly toasts = inject(ToastService);
  private readonly document = inject(DOCUMENT);
  private readonly dialogRef = injectDialogRef();
  private readonly builder = inject(FormBuilder);

  protected readonly form: FormGroup = this.builder.group({
    question: [this.alwaysId, [Validators.required]],
    value: [this.alwaysId, [Validators.required]],
  });

  /** Mirrors the `question` control so the option list can be a computed rather than a template call. */
  private readonly $dependsOn = signal(this.alwaysId);

  protected readonly $question = computed(() => this.$template()?.template?.questions?.find(entry => entry._id === this.$questionId()) ?? null);

  /**
   * A stable array per template, not a fresh one per change-detection pass: `ng-select` compares
   * `[items]` by reference, and rebuilding it every pass resets the open dropdown.
   */
  protected readonly $conditionQuestions = computed<ConditionQuestion[]>(() => [
    { _id: this.alwaysId, name: 'Always Show', options: [{ _id: this.alwaysId, name: 'Always' }] },
    ...(this.$template()?.template?.questions ?? [])
      .filter(candidate => candidate.type === JobTemplateQuestionType.Dropdown)
      .filter(candidate => candidate._id !== this.$questionId())
      .filter(candidate => candidate.options?.length > 0)
      .map(candidate => ({ _id: candidate._id, name: candidate.name, options: candidate.options.map(option => ({ _id: option._id, name: option.name })) })),
  ]);

  protected readonly $options = computed<Entity[]>(() => this.$conditionQuestions().find(candidate => candidate._id === this.$dependsOn())?.options ?? []);

  protected readonly $hasRule = computed(() => this.$dependsOn() !== this.alwaysId);

  public constructor() {

    DesignSystem.Mount(this.document, jobTemplateQuestionVisibilityStyleId, jobTemplateQuestionVisibilityBridgeStyles);

    // Seeded here, not in the field initialisers: a signal input has no value until the first change
    // detection, so the stored rule cannot be read while the form is being built.
    effect(() => {
      const stored = this.$question()?.showCondition;
      untracked(() => {
        const condition = stored ?? { question: this.alwaysId, value: this.alwaysId };
        this.form.patchValue(condition, { emitEvent: false });
        this.$dependsOn.set(condition.question);
        this.form.markAsPristine();
      });
    });

    // Changing the question invalidates whatever option was chosen against the previous one.
    this.form
      .get('question')
      .valueChanges
      .pipe(
        untilDestroyed(this),
        tap(questionId => {
          this.$dependsOn.set(questionId);
          this.form.get('value').patchValue(untracked(this.$options)[0]?._id);
        }),
      )
      .subscribe();
  }

  protected cancel(): void {

    this.dialogRef.close();
  }

  protected async save(): Promise<void> {

    const value = this.form.value as JobTemplateQuestionVisibilityCondition;
    // Null, not a rule that matches everything — an absent rule is what legacy stores for the sentinel.
    const showCondition = value?.question !== this.alwaysId ? value : null;

    try {
      await lastValueFrom(this.entityData.executeCommand(ActivityEntityName.JobTemplateQuestions, JobTemplateQuestionCommandId.PatchQuestion, { questionId: this.$questionId(), patch: { showCondition } }));
    } catch (error: unknown) {
      this.toasts.error('Visibility Rule', error instanceof Error ? error.message : 'The visibility rule could not be saved.');
      return;
    }

    await this.queryClient.invalidateQueries({ queryKey: JOB_TEMPLATE_QUESTION_QUERY_KEY, refetchType: 'all' });
    this.$questionsChanged()();
    this.dialogRef.close();
  }
}
